import {AfterViewInit, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ReCaptchaV3Service} from 'ngx-captcha';
import {Subscription} from 'rxjs';
import {DoktorseReceptService} from '../../../services/doktorse-recept.service';
import {LoaderService} from '../../../services/loader.service';
import {ToolsService} from '../../../services/tools.service';
import {DoktorseReceptPayload} from '../../../types/DoktorseReceptPayload';
import {GtmEventEnum} from '../../../enums/GoogleEnums';
import {finalize} from 'rxjs/operators';
import {LoaderQueueItem} from '../../../types/LoaderQueueItem';
import {GtmService} from 'src/app/services/gtm.service';
import {DataLayerParamsClickGA4} from '../../../types/DataLayerParamsClickGA4';
import {EventCategoryEnum} from '../../../enums/GaTrackingEnums';
import {GtmGa4EventPushService} from '../../../services/gtm-ga4-event-push.service';
import {AppConfigService} from '../../../services/app-config.service';
import {DeviceTypeEnum} from '../../../enums/DeviceTypeEnum';
import {DLPageTypeEnum} from '../../../enums/DataLayerEnums';

@Component({
  selector: 'app-doktorse-recept',
  templateUrl: './doktorse-recept.component.html',
  styleUrls: ['./doktorse-recept.component.scss'],
})
export class DoktorseReceptComponent implements OnInit, OnDestroy, AfterViewInit {
  public receptForm: UntypedFormGroup;
  public isSaved = false;
  public isMobile = false;
  public isError = false;
  public siteKey: string;
  protected readonly DLPageTypeEnum = DLPageTypeEnum;
  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public pageType: DLPageTypeEnum,
    public dialogRef: MatDialogRef<DoktorseReceptComponent>,
    private formBuilder: UntypedFormBuilder,
    private loaderService: LoaderService,
    private doktorseReceptService: DoktorseReceptService,
    private gtmService: GtmService,
    private tools: ToolsService,
    private reCaptchaV3Service: ReCaptchaV3Service,
    private gtmEventPush: GtmGa4EventPushService,
    private appConfigService: AppConfigService,
  ) {
    this.siteKey = appConfigService.appConfig.recaptchaSiteKey;
  }

  ngOnInit(): void {
    this.isSaved = false;
    this.isError = false;
    this.isMobile = this.tools.getDeviceType() === DeviceTypeEnum.MOBILE;
    this.buildForm();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngAfterViewInit(): void {
    this.tools.recaptchaFix();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  saveQuestion(): void {
    this.isError = false;

    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.DOKTORSE_MODAL,
      Action: 'klick skicka',
      Label: 'telephone: ' + this.receptForm.value.phoneNumber,
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);

    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: EventCategoryEnum.DOKTORSE_MODAL,
      pageType: this.pageType,
    };
    const gtmGa4Tag = {
      event: GtmEventEnum.CTA_CLICK,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);
    this.reCaptchaV3Service.execute(
      this.siteKey,
      'send_recept_request',
      (token) => {
        this.receptForm.controls.recaptcha.setValue(token || '');

        (Object as any).values(this.receptForm.controls).forEach((control) => control.markAllAsTouched());
        if (this.receptForm.valid) {
          const queryId: number = this.loaderService.generateQueueId();
          const queryItem: LoaderQueueItem = {
            id: queryId,
            title: '',
            description: '',
          };
          this.loaderService.addToQueue(queryItem);
          this.subscriptions.push(
            this.doktorseReceptService
              .SendReceptRequest(this.getObjectFromFormData())
              .pipe(
                finalize(() => {
                  this.loaderService.removeFromQueue(queryId);
                }),
              )
              .subscribe(
                (result) => this.processQuestionData(result),
                (error) => {
                  console.error(error);
                  this.isError = true;
                },
              ),
          );
        }
      },
      {
        useGlobalDomain: false,
      },
      (error) => console.log(error),
    );
  }

  private processQuestionData(result: DoktorseReceptPayload): void {
    this.isSaved = true;
    this.isError = false;
    this.dialogRef.close();
  }

  private buildForm(): void {
    this.receptForm = this.formBuilder.group({
      phoneNumber: ['', [Validators.required]],
      recaptcha: ['', [Validators.required]],
    });
  }

  private getObjectFromFormData(): DoktorseReceptPayload {
    const payload: DoktorseReceptPayload = {
      phoneNumber: this.receptForm.value.phoneNumber,
      'g-recaptcha-response': this.receptForm.value.recaptcha,
    };
    return payload;
  }
}
